import React, { useRef } from 'react';
import { Button, DatePicker, Skeleton } from 'antd';
import { PrinterOutlined } from '@ant-design/icons';
import { useParams, useSearchParams } from 'react-router-dom';
import { DriverCardEventType, useDriverCard } from './use-driver-card';
import { Page } from '../../components.js';
import styled from 'styled-components';
import { TaskType } from 'types';
import { grey } from '@ant-design/colors';
import { DriverCardEventTile } from './driver-card-event-tile';
import { Formatting } from '../../utils';
import moment from 'moment';
import { useReactToPrint } from 'react-to-print';
import { DriverCardPrint } from '../../prints/driver-card-print';

const Wrapper = styled.div`
	display: flex;
	flex-direction: column;
	width: 100%;
	flex: 1;
	min-height: 0;
	overflow-y: auto;

	> div {
		width: 100%;
		display: flex;
		flex-direction: column;
		margin-bottom: 24px;

		> h3 {
			margin: 16px 0 8px 0;
			font-size: 20px;
			text-align: center;
		}
	}
`;

const DaySection = styled.div`
	width: 100%;
	display: flex;
	flex-direction: column;
	margin-bottom: 32px;

	> h2 {
		margin: 24px 0 8px 0;
		font-size: 22px;
		text-align: center;
		border-bottom: 2px solid #d9d9d9;
		padding-bottom: 8px;
	}

	> div {
		width: 100%;
		display: flex;
		flex-direction: column;
		margin-bottom: 16px;

		> h3 {
			margin: 12px 0 6px 0;
			font-size: 18px;
			text-align: center;
		}
	}
`;

const OrderEvent = styled.div`
	display: flex;
	width: 100%;
	padding: 12px;
	border-radius: 6px;
	background: ${({ color }) => color};
	color: white;
	margin-bottom: 4px;

	> div {
		flex: 1;
		display: flex;
		flex-direction: column;
		justify-content: center;
	}

	h4 {
		font-size: 18px;
		margin: 0;
		margin-bottom: 4px;
		color: white;
	}

	h5 {
		font-size: 18px;
		font-weight: 400;
		margin: 0;
		color: white;
	}

	:hover {
		cursor: pointer;
	}
`;

export const DriverCard: React.FC = () => {
	const { id: idRaw } = useParams();
	const [searchParams, setSearchParams] = useSearchParams();

	const id = parseInt(idRaw || '0', 10);

	const fromParam = searchParams.get('from');
	const toParam = searchParams.get('to');
	const from = fromParam ? moment(fromParam, 'YYYY-MM-DD') : moment().startOf('month');
	const to = toParam ? moment(toParam, 'YYYY-MM-DD') : moment().endOf('month');

	const { driver, days, summary } = useDriverCard(id, from, to);

	const printRef = useRef<HTMLDivElement>(null);
	const handlePrint = useReactToPrint({
		content: () => printRef.current,
	});

	const actions = [
		<DatePicker
			key="from"
			value={from}
			allowClear={false}
			placeholder="Od"
			onChange={(value) => {
				if (value) setSearchParams({ from: value.format('YYYY-MM-DD'), to: to.format('YYYY-MM-DD') });
			}}
		/>,
		<DatePicker
			key="to"
			value={to}
			allowClear={false}
			placeholder="Do"
			onChange={(value) => {
				if (value) setSearchParams({ from: from.format('YYYY-MM-DD'), to: value.format('YYYY-MM-DD') });
			}}
		/>,
		<Button key="print" size="large" type="primary" icon={<PrinterOutlined />} onClick={handlePrint}>
			Drukuj
		</Button>,
	];

	if (!driver || !days || !summary) {
		return (
			<Page title="" actions={actions}>
				<Skeleton active />
			</Page>
		);
	}

	return (
		<>
			<Page title={`Pracownicy / ${driver.name} ${driver.surname}`} actions={actions}>
				<Wrapper>
					{days.length === 0 ? (
						<div style={{ textAlign: 'center', padding: '32px', color: '#999' }}>
							Brak aktywności w wybranym zakresie
						</div>
					) : (
						days.map((day) => (
							<DaySection key={day.key}>
								<h2>{day.date.format('dddd, D MMMM YYYY')}</h2>

								<div>
									<h3>Podstawienia</h3>
									{day.events
										.filter(
											(e) =>
												(e.type === DriverCardEventType.classicOrder || e.type === DriverCardEventType.looseOrder) &&
												e.task.type === TaskType.delivery
										)
										.map((e) => (
											<DriverCardEventTile key={e.key} event={e} />
										))}
								</div>

								<div>
									<h3>Odbiory</h3>
									{day.events
										.filter(
											(e) =>
												(e.type === DriverCardEventType.classicOrder || e.type === DriverCardEventType.looseOrder) &&
												e.task.type === TaskType.claiming
										)
										.map((e) => (
											<DriverCardEventTile key={e.key} event={e} />
										))}
								</div>

								<div>
									<h3>Wymiany</h3>
									{day.events
										.filter(
											(e) =>
												(e.type === DriverCardEventType.classicOrder || e.type === DriverCardEventType.looseOrder) &&
												e.task.type === TaskType.replacement
										)
										.map((e) => (
											<DriverCardEventTile key={e.key} event={e} />
										))}
								</div>

								<div>
									<h3>Wydatki</h3>
									{day.events
										.filter((e) => e.type === DriverCardEventType.expense)
										.map((e) => (
											<DriverCardEventTile key={e.key} event={e} />
										))}
								</div>

								<div>
									<h3>Wydania gotówki</h3>
									{day.events
										.filter((e) => e.type === DriverCardEventType.settlement)
										.map((e) => (
											<DriverCardEventTile key={e.key} event={e} />
										))}
								</div>
							</DaySection>
						))
					)}

					<div>
						<h3>Podsumowanie</h3>
						<OrderEvent color={grey[5]}>
							<div>
								<h4>Pobrane od klientów: {Formatting.money(summary.advances + summary.payments)}</h4>
								<h4>Wydane: {Formatting.money(summary.spent + summary.settlements)}</h4>
							</div>
							<div>
								<span>Zaliczki: {Formatting.money(summary.advances)}</span>
								<span>Zapłaty: {Formatting.money(summary.payments)}</span>
								<span>Wydatki: {Formatting.money(summary.spent)}</span>
								<span>Pobrania gotówki: {Formatting.money(summary.settlements)}</span>
							</div>
						</OrderEvent>
					</div>

					<div>
						<h3>
							Balans: {Formatting.money(summary.advances + summary.payments - summary.spent - summary.settlements)}
						</h3>
						<h3>Do pobrania: {Formatting.money(driver.uncollectedCash ?? 0)}</h3>
					</div>
				</Wrapper>
			</Page>

			<DriverCardPrint ref={printRef} driver={driver} days={days} summary={summary} from={from} to={to} />
		</>
	);
};
